// Raise an issue from a search result, in the world you mean it in.
//
// Split search is the one place in the product where a question is
// answered by BOTH worlds at once - "P-101" comes back as text on a
// sheet and as elements in the model. That is exactly the moment a
// coordinator decides something is wrong, and until now the only way to
// act on it was to leave, open the issue page, and retype what they had
// just been looking at.
//
// The thing that has to be got right is WHICH WORLD the issue is
// against, because that decides what the issue can later be resolved
// with. An issue raised on a sheet resolves with a reissued sheet; one
// raised on the model resolves with a push. So the source is picked
// deliberately - 2d, 3d, or both - rather than inferred from whichever
// pane happened to be clicked.
//
// "Both" is offered only when both worlds actually returned something,
// and it is recorded as sourceType "hybrid" with each side's own
// reference. It still does NOT claim the two hits are the same object -
// nothing in this feature ever has - and the description says so in the
// words the page uses, so an issue read next month is not more
// confident than the index that produced it.
import { url, tokenRef } from "api";

const auth = () => {
  const t = localStorage.getItem(tokenRef);
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// How much of the sheet to show around the hit. A matched word on a
// 3000pt sheet is a couple of pixels: cropped tight it is unreadable and
// unplaceable, so the snapshot keeps enough drawing around it to say
// WHERE on the sheet this is.
const CROP_MIN_PX = 900;
const SNAP_MAX_PX = 1600;

/**
 * A PNG of a 2D hit in its context on the sheet, as a data URL.
 *
 * `img` is the page raster already on screen. It is a blob: URL from an
 * authenticated fetch - same origin - so drawing it into a canvas does
 * not taint it and toDataURL works. Returns "" if there is nothing to
 * capture, which callers treat as "create the issue anyway".
 */
export function sheetSnapshot(img, hit) {
  try {
    if (!img || !img.naturalWidth || !hit || !hit.bbox || !hit.pageWidth) return "";
    const ptToPx = img.naturalWidth / hit.pageWidth;
    const [x0, y0, x1, y1] = hit.bbox;
    const bx = x0 * ptToPx, by = y0 * ptToPx;
    const bw = Math.max(1, (x1 - x0) * ptToPx), bh = Math.max(1, (y1 - y0) * ptToPx);

    // A window around the hit, clamped to the page.
    const want = Math.max(CROP_MIN_PX, bw * 8, bh * 8);
    const cw = Math.min(img.naturalWidth, want);
    const ch = Math.min(img.naturalHeight, Math.round(want * 0.7));
    let sx = Math.round(bx + bw / 2 - cw / 2);
    let sy = Math.round(by + bh / 2 - ch / 2);
    sx = Math.max(0, Math.min(sx, img.naturalWidth - cw));
    sy = Math.max(0, Math.min(sy, img.naturalHeight - ch));

    const scale = Math.min(1, SNAP_MAX_PX / cw);
    const c = document.createElement("canvas");
    c.width = Math.round(cw * scale);
    c.height = Math.round(ch * scale);
    const g = c.getContext("2d");
    g.fillStyle = "#fff";
    g.fillRect(0, 0, c.width, c.height);
    g.drawImage(img, sx, sy, cw, ch, 0, 0, c.width, c.height);

    // The highlight, drawn at the hit's true size with a ring around it
    // so a two-pixel word is still findable in the picture.
    const hx = (bx - sx) * scale, hy = (by - sy) * scale;
    const hw = bw * scale, hh = bh * scale;
    g.strokeStyle = "#fa541c";
    g.lineWidth = 2;
    g.strokeRect(hx, hy, Math.max(2, hw), Math.max(2, hh));
    const r = Math.max(18, Math.max(hw, hh));
    g.strokeStyle = "rgba(250,84,28,0.45)";
    g.lineWidth = 2;
    g.beginPath();
    g.arc(hx + hw / 2, hy + hh / 2, r, 0, Math.PI * 2);
    g.stroke();
    return c.toDataURL("image/png");
  } catch (e) {
    // A snapshot is never worth failing the issue for.
    return "";
  }
}

function dataUrlToBlob(dataUrl) {
  const [head, b64] = String(dataUrl).split(",");
  const mime = (head.match(/data:([^;]+)/) || [, "image/png"])[1];
  const bin = atob(b64 || "");
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return new Blob([buf], { type: mime });
}

const where = (h) => [
  h && h.level ? h.level : "",
  h && h.drawingGrid && h.drawingGrid.col && h.drawingGrid.row
    ? `grid ${h.drawingGrid.col}-${h.drawingGrid.row}` : "",
].filter(Boolean).join(" · ");

/** A one-line name for a hit, for titles and option labels. */
export function hitLabel(h, world) {
  if (!h) return "";
  if (world === "3d") {
    return [h.kind || "element", h.text, where(h)].filter(Boolean).join(" · ");
  }
  return [
    [h.sheetNumber || "sheet", h.page ? `p${h.page + 1}` : ""].filter(Boolean).join(" "),
    h.text, h.cell || "", where(h),
  ].filter(Boolean).join(" · ");
}

/** A title that says what was found and where, not "Issue from search". */
export function suggestTitle({ query, source, hit2d, hit3d }) {
  const q = String(query || "").trim();
  const subject = q || (hit2d && hit2d.text) || (hit3d && hit3d.text) || "search result";
  if (source === "2d" && hit2d) return `${subject} on ${hit2d.sheetNumber || "sheet"}`;
  if (source === "3d" && hit3d) return `${subject} in the model${hit3d.level ? ` · ${hit3d.level}` : ""}`;
  if (source === "hybrid") return `${subject} — sheet and model`;
  return subject;
}

/**
 * The description. Written so the issue still means something when it
 * is read with the search closed: what was asked, what each world
 * returned, and the standing caveat about what agreement is.
 */
export function describeSearchIssue({ query, source, hit2d, hit3d, agreed = [] }) {
  const lines = [];
  if (query) lines.push(`Raised from a search for "${query}" in Find in Sheets & Model.`);
  else lines.push("Raised from Find in Sheets & Model.");
  lines.push("");

  if ((source === "2d" || source === "hybrid") && hit2d) {
    lines.push("On the sheet:");
    lines.push(`  ${hit2d.sheetNumber || "(unnumbered sheet)"}${hit2d.page ? ` page ${hit2d.page + 1}` : ""}`);
    if (hit2d.text) lines.push(`  matched text: ${hit2d.text}`);
    if (hit2d.cell) lines.push(`  title-block cell: ${hit2d.cell}`);
    if (where(hit2d)) lines.push(`  ${where(hit2d)}`);
    if ((hit2d.tags || []).length) lines.push(`  tags: ${hit2d.tags.join(" · ")}`);
    lines.push("");
  }

  if ((source === "3d" || source === "hybrid") && hit3d) {
    lines.push("In the model:");
    lines.push(`  ${hit3d.kind || "element"}${hit3d.text ? ` — ${hit3d.text}` : ""}`);
    if (hit3d.uniqueId) lines.push(`  uniqueId: ${hit3d.uniqueId}`);
    if (where(hit3d)) lines.push(`  ${where(hit3d)}`);
    if ((hit3d.tags || []).length) lines.push(`  tags: ${hit3d.tags.join(" · ")}`);
    lines.push("");
  }

  if (source === "hybrid") {
    lines.push(
      agreed && agreed.length
        ? `Both worlds returned ${agreed.join(", ")}. That is agreement between two indexes, `
          + "not proof that the sheet text and the model element are the same object."
        : "Recorded against both worlds. Nothing here claims the sheet text and the model "
          + "element are the same object."
    );
  }
  return lines.join("\n").trim();
}

/**
 * Where a hit puts the issue on the project: the location fields the
 * issue page and the Gantt lane read. Taken from the hit that owns the
 * source, so a 3D-sourced issue is placed by the model's level and a
 * 2D-sourced one by the sheet's grid.
 */
function locationFrom(source, hit2d, hit3d) {
  const h = source === "3d" ? (hit3d || hit2d) : (hit2d || hit3d);
  if (!h) return undefined;
  const g = h.drawingGrid && h.drawingGrid.col && h.drawingGrid.row
    ? `${h.drawingGrid.col}-${h.drawingGrid.row}` : "";
  if (!h.level && !g) return undefined;
  return { area: "", level: h.level || "", grid: g };
}

/**
 * Create the issue. `source` is "2d" | "3d" | "hybrid" and decides which
 * references are written - the caller has already made that choice
 * explicitly rather than it being read off whichever pane was clicked.
 */
export async function createIssueFromSearch({
  projectId, source, hit2d, hit3d, query, agreed = [],
  title, description, priority = "normal", dueDate, snapshot = "", estimatedHours,
}) {
  const body = {
    title: title || suggestTitle({ query, source, hit2d, hit3d }),
    description: description || describeSearchIssue({ query, source, hit2d, hit3d, agreed }),
    projectId,
    sourceType: source,
    priority,
    // Required from the web pages (owner, 2026-10-16); the marker is what
    // the server holds to the rule.
    estimatedHours,
    createdFrom: "web",
  };
  if (dueDate) body.dueDate = dueDate;
  const loc = locationFrom(source, hit2d, hit3d);
  if (loc) body.location = loc;

  // The 2D side: the Doc Control record IS the sheet, so the issue
  // points at it rather than mirroring a copy of the file.
  if ((source === "2d" || source === "hybrid") && hit2d && hit2d.docId) {
    body.sourceDocControlId = hit2d.docId;
    body.sourceDocument = {
      name: hit2d.sheetName || hit2d.sheetNumber || "Sheet",
      sheetNumber: hit2d.sheetNumber || "",
    };
  }
  // The 3D side: the element the hit is, so the viewer can select it.
  if ((source === "3d" || source === "hybrid") && hit3d && hit3d.uniqueId) {
    body.sourceModelViewer = { selectedElements: [String(hit3d.uniqueId)] };
    body.openedAtSnapshotElements = [{
      uniqueId: String(hit3d.uniqueId),
      fragmentHash: hit3d.fragmentHash || "",
      tileKey: hit3d.tileId || "",
      level: hit3d.level || "",
    }];
  }

  const res = await fetch(`${url}/issue/`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...auth() },
    body: JSON.stringify(body),
  });
  const created = await res.json();
  if (!res.ok) throw new Error(created.err || "Could not create the issue");
  const issue = created.data || created.issue || created;
  if (!issue || !issue._id) throw new Error("The issue came back without an id");

  let snapshotFailed = !snapshot;
  if (snapshot) {
    try {
      const fd = new FormData();
      fd.append("files", dataUrlToBlob(snapshot), `search-${Date.now()}.png`);
      const up = await fetch(`${url}/issue/${issue._id}/attachments`, {
        method: "POST", headers: auth(), body: fd,
      });
      snapshotFailed = !up.ok;
    } catch (e) {
      // The issue exists. Losing the picture is worth saying out loud
      // rather than failing the whole action.
      snapshotFailed = true;
    }
  }
  return { issue, href: issueHref(issue), snapshotFailed };
}

// /issue/:x takes a SHARE TOKEN, not an id - the same trap
// helpers/issueFromDiff documents, and the same answer.
export function issueHref(issue) {
  if (!issue) return "/issues";
  return issue.shareToken
    ? `/issue/${issue.shareToken}/resolve`
    : `/issue-viewer/${issue._id}`;
}
